import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    IFlujoDefinicion,
    IHallazgoValidacion,
    INodo,
    IOpcion,
    NODO_TIPO,
    NODO_TIPO_LABEL,
    NodoTipo,
    crearNodoVacio,
} from 'src/app/models/notificacion/whatsapp_flujo.model';
import {
    construirFormGroupNodo,
    construirFormGroupVersion,
    extraerDefinicionDeFormGroup,
    nodosFormArray,
    opcionesFormArray,
} from './flujo-form.util';
import { INodoDestinoDisponible } from './components/flujo-nodo-editor/flujo-nodo-editor.component';
import {
    IPendiente,
    PENDIENTE,
    TIPOS_PARA_RESOLVER,
    calcularPendientes,
    nodosInalcanzables,
    nodosSinFinal,
} from './flujo-pendientes.util';


const ID_NODO_INICIAL = 'inicio';
const TIPO_BOTON_QUICK_REPLY = 'QUICK_REPLY';
const LARGO_MAX_ETIQUETA_NODO = 40;

/**
 * Lo que el bot repite si el cliente escribe en vez de tocar un botón. Viene
 * cargado porque sirve igual para cualquier plantilla y no hay nada que decidir
 * ahí; el usuario lo puede cambiar.
 */
const TEXTO_REINTENTO_POR_DEFECTO = 'Por favor elegí una de las opciones.';

/**
 * Editor del flujo de una plantilla. El padre lo consulta con
 * `obtenerDefinicion()` al guardar; no emite por cambio, porque el eco del
 * `@Input` reconstruía el formulario y cerraba el panel mientras se escribía.
 * `definicion` es solo el valor inicial. El nodo inicial no se edita: sus
 * opciones se derivan de los botones de la plantilla.
 */
@Component({
    selector: 'app-flujo-editor',
    templateUrl: './flujo-editor.component.html',
})
export class FlujoEditorComponent implements OnChanges {

    readonly NODO_TIPO = NODO_TIPO;
    readonly ID_NODO_INICIAL = ID_NODO_INICIAL;
    readonly PENDIENTE = PENDIENTE;

    /** Tipos ofrecidos al resolver una punta suelta, con su nombre de usuario. */
    readonly tiposParaResolver = TIPOS_PARA_RESOLVER.map((tipo) => ({
        label: NODO_TIPO_LABEL[tipo],
        value: tipo,
    }));

    @Input() botones: Record<string, any>[] | null = null;
    @Input() definicion: IFlujoDefinicion | null = null;
    @Input() bloqueado = false;
    @Input() motivoBloqueo = '';
    @Input() hallazgos: IHallazgoValidacion[] = [];

    /**
     * Avisa cuando el usuario prende o apaga el flujo. Con el flujo activo los
     * botones de la plantilla pasan a ser un mensaje interactivo de Meta, que
     * admite menos botones y etiquetas más cortas que una plantilla.
     */
    @Output() activoChange = new EventEmitter<boolean>();

    versionForm: FormGroup | null = null;

    sidebarVisible = false;
    ayudaVisible = false;
    nodoSeleccionadoIndice: number | null = null;

    private derivadoCache: {
        firma: string;
        valores: {
            pendientes: IPendiente[];
            nodosDisponibles: INodoDestinoDisponible[];
            disparadores: Record<string, string>;
            nodosSinFinal: Set<string>;
            nodosInalcanzables: Set<string>;
        };
    } | null = null;

    constructor(private fb: FormBuilder) {}

    get opcionesDeLaPlantilla(): IOpcion[] {
        if (!Array.isArray(this.botones)) {
            return [];
        }

        return this.botones
            .filter((boton) => String(boton?.tipo || '').toUpperCase() === TIPO_BOTON_QUICK_REPLY)
            .map((boton) => String(boton?.texto || '').trim())
            .filter((texto) => texto.length > 0)
            .map((texto) => ({ id: texto, etiqueta: texto, destinoNodoId: '' }));
    }

    get plantillaTieneBotones(): boolean {
        return this.opcionesDeLaPlantilla.length > 0;
    }

    get activo(): boolean {
        return this.versionForm !== null;
    }

    get nodoFormSeleccionado(): FormGroup | null {
        if (this.nodoSeleccionadoIndice === null || !this.versionForm) {
            return null;
        }
        const controles = nodosFormArray(this.versionForm).controls;
        return (controles[this.nodoSeleccionadoIndice] as FormGroup) || null;
    }

    get nodoSeleccionadoEsInicial(): boolean {
        return this.nodoFormSeleccionado?.get('id')?.value === ID_NODO_INICIAL;
    }

    /** Lo que el padre guarda. Es la única salida de este componente. */
    obtenerDefinicion(): IFlujoDefinicion | null {
        return this.versionForm ? extraerDefinicionDeFormGroup(this.versionForm) : null;
    }

    get nodosDisponibles(): INodoDestinoDisponible[] {
        return this.derivado().nodosDisponibles;
    }

    /** Lo que todavía falta para cerrar el circuito. La lista que guía al usuario. */
    get pendientes(): IPendiente[] {
        return this.derivado().pendientes;
    }

    /** Qué botón lleva a cada paso, para titular la tarjeta por su causa. Si dos llevan al mismo, gana el primero. */
    get disparadores(): Record<string, string> {
        return this.derivado().disparadores;
    }

    /** Pasos desde los que ningún camino llega a un END/HANDOFF (ciclo cerrado sin salida). La tarjeta lo necesita para dejar de mostrar "Listo". */
    get nodosSinFinal(): Set<string> {
        return this.derivado().nodosSinFinal;
    }

    /** Pasos a los que ningún camino desde el inicio llega. Mismo criterio que `NODO_INALCANZABLE` en el backend. */
    get nodosInalcanzables(): Set<string> {
        return this.derivado().nodosInalcanzables;
    }

    get circuitoCerrado(): boolean {
        return this.activo && this.pendientes.length === 0;
    }

    /** Identidad estable para el `*ngFor` de pendientes: sin esto se recrean las filas en cada ciclo. */
    trackPendiente(_indice: number, pendiente: IPendiente): string {
        return `${pendiente.codigo}|${pendiente.indiceNodo}|${pendiente.indiceOpcion}|${pendiente.titulo}`;
    }

    /**
     * Lo que la vista deriva del flujo, memoizado POR REFERENCIA. No es una
     * optimización: alimenta `[options]` de varios `p-dropdown` y un `*ngFor`, y
     * devolver un array nuevo en cada ciclo de detección de cambios cuelga la
     * pantalla.
     */
    private derivado(): {
        pendientes: IPendiente[];
        nodosDisponibles: INodoDestinoDisponible[];
        disparadores: Record<string, string>;
        nodosSinFinal: Set<string>;
        nodosInalcanzables: Set<string>;
    } {
        const definicion = this.obtenerDefinicion();
        const firma = JSON.stringify(definicion);

        if (this.derivadoCache && this.derivadoCache.firma === firma) {
            return this.derivadoCache.valores;
        }

        const nodos = definicion?.nodos ?? [];

        const disparadores: Record<string, string> = {};
        for (const nodo of nodos) {
            for (const opcion of (nodo.opciones || []) as IOpcion[]) {
                const destino = String(opcion?.destinoNodoId || '');
                if (destino && !disparadores[destino]) {
                    disparadores[destino] = opcion.etiqueta || opcion.id;
                }
            }
        }

        const valores = {
            pendientes: calcularPendientes(definicion, ID_NODO_INICIAL),
            nodosDisponibles: nodos.map((nodo, indice) => ({
                id: nodo.id || '',
                label: this.etiquetaDeNodo(nodo, indice),
            })),
            disparadores,
            nodosSinFinal: nodosSinFinal(nodos),
            nodosInalcanzables: nodosInalcanzables(nodos, ID_NODO_INICIAL),
        };

        this.derivadoCache = { firma, valores };
        return valores;
    }

    /** Crea el paso que le falta a una punta y la apunta ahí. Abre el panel: el paso nace sin texto. */
    resolverCreando(pendiente: IPendiente, tipo: NodoTipo): void {
        if (!this.versionForm || tipo === null || tipo === undefined) {
            return;
        }

        const array = nodosFormArray(this.versionForm);
        const id = this.proximoIdDePaso(array.controls.map((control) => control.get('id')?.value));

        array.push(construirFormGroupNodo(this.fb, { ...crearNodoVacio(id), tipo }));
        this.apuntarPendienteA(pendiente, id);
        this.editarNodo(array.length - 1);
    }

    /** Resuelve una punta mandándola a un paso que ya existe: dos botones pueden terminar en el mismo lugar. */
    resolverConExistente(pendiente: IPendiente, nodoId: string): void {
        if (!nodoId) {
            return;
        }

        this.apuntarPendienteA(pendiente, nodoId);
    }

    /** Abre el paso de un pendiente para escribirle el texto o agregarle botones. */
    abrirPendiente(pendiente: IPendiente): void {
        if (pendiente.indiceNodo === null) {
            return;
        }

        this.editarNodo(pendiente.indiceNodo);
    }

    ngOnChanges(cambios: SimpleChanges): void {
        if (cambios['definicion']) {
            this.cargarDefinicion(this.definicion);
        }

        if (cambios['botones'] && this.versionForm) {
            this.sincronizarNodoInicialConBotones();
        }
    }

    onToggleActivo(activar: boolean): void {
        if (!activar) {
            this.versionForm = null;
            this.sidebarVisible = false;
            this.nodoSeleccionadoIndice = null;
            this.activoChange.emit(false);
            return;
        }

        this.versionForm = construirFormGroupVersion(this.fb, {
            nodoInicialId: ID_NODO_INICIAL,
            nodos: [this.construirNodoInicial([])],
        });

        this.sincronizarNodoInicialConBotones();
        this.activoChange.emit(true);
    }

    agregarNodo(): void {
        if (!this.versionForm) {
            return;
        }

        const array = nodosFormArray(this.versionForm);
        const id = this.proximoIdDePaso(array.controls.map((control) => control.get('id')?.value));

        array.push(construirFormGroupNodo(this.fb, crearNodoVacio(id)));
        this.editarNodo(array.length - 1);
    }

    /** El id interno del próximo paso. No se muestra nunca: solo sirve para que las aristas lo referencien. */
    private proximoIdDePaso(idsExistentes: string[]): string {
        const usados = new Set(idsExistentes);
        let contador = usados.size;
        let id = `paso_${contador}`;

        while (usados.has(id)) {
            contador += 1;
            id = `paso_${contador}`;
        }

        return id;
    }

    /** Deja la punta suelta apuntando a `destinoId`, sea un botón o el siguiente de un mensaje. */
    private apuntarPendienteA(pendiente: IPendiente, destinoId: string): void {
        if (!this.versionForm || pendiente.indiceNodo === null) {
            return;
        }

        const nodoForm = nodosFormArray(this.versionForm).at(pendiente.indiceNodo) as FormGroup;
        if (!nodoForm) {
            return;
        }

        if (pendiente.indiceOpcion !== null) {
            opcionesFormArray(nodoForm).at(pendiente.indiceOpcion)?.get('destinoNodoId')?.setValue(destinoId);
            return;
        }

        nodoForm.get('siguienteNodoId')?.setValue(destinoId);
    }

    editarNodo(indice: number): void {
        this.nodoSeleccionadoIndice = indice;
        this.sidebarVisible = true;
    }

    eliminarNodo(indice: number): void {
        if (!this.versionForm) {
            return;
        }

        const array = nodosFormArray(this.versionForm);
        if (array.controls[indice]?.get('id')?.value === ID_NODO_INICIAL) {
            return;
        }

        array.removeAt(indice);
        this.sidebarVisible = false;
        this.nodoSeleccionadoIndice = null;
    }

    private cargarDefinicion(definicion: IFlujoDefinicion | null): void {
        this.sidebarVisible = false;
        this.nodoSeleccionadoIndice = null;

        if (!definicion || !Array.isArray(definicion.nodos) || definicion.nodos.length === 0) {
            this.versionForm = null;
            return;
        }

        this.versionForm = construirFormGroupVersion(this.fb, definicion);
        this.sincronizarNodoInicialConBotones();
    }

    /**
     * Reconstruye las opciones del nodo inicial a partir de los botones
     * actuales de la plantilla, conservando el destino ya elegido para los
     * botones que siguen existiendo.
     *
     * Pública: el padre (`whatsapp-template-ventana`) edita `botones` como un
     * arreglo mutado en el lugar (push/splice/`ngModel` sobre `boton.texto`),
     * así que la referencia nunca cambia y `ngOnChanges` no se vuelve a
     * disparar solo. El padre llama a este método directo después de cada
     * cambio; `ngOnChanges` sigue cubriendo el caso en que sí llega una
     * referencia nueva (p. ej. la carga inicial).
     */
    sincronizarNodoInicialConBotones(): void {
        if (!this.versionForm) {
            return;
        }

        const array = nodosFormArray(this.versionForm);
        const indice = array.controls.findIndex((control) => control.get('id')?.value === ID_NODO_INICIAL);

        const destinosPrevios = new Map<string, string>();
        if (indice >= 0) {
            const opciones = (array.controls[indice] as FormGroup).getRawValue().opciones as IOpcion[];
            for (const opcion of opciones || []) {
                destinosPrevios.set(opcion.id, opcion.destinoNodoId);
            }
        }

        const opciones = this.opcionesDeLaPlantilla.map((opcion) => ({
            ...opcion,
            destinoNodoId: destinosPrevios.get(opcion.id) || '',
        }));

        const nodoInicial = construirFormGroupNodo(this.fb, this.construirNodoInicial(opciones));
        const textoPrevio = indice >= 0 ? array.controls[indice]?.get('texto')?.value : '';
        nodoInicial.get('texto')?.setValue(textoPrevio || TEXTO_REINTENTO_POR_DEFECTO);

        if (indice >= 0) {
            array.setControl(indice, nodoInicial);
        } else {
            array.insert(0, nodoInicial);
        }

        this.versionForm.get('nodoInicialId')?.setValue(ID_NODO_INICIAL);
    }

    private construirNodoInicial(opciones: IOpcion[]): INodo {
        return {
            ...crearNodoVacio(ID_NODO_INICIAL),
            tipo: NODO_TIPO.CHOICE,
            opciones,
        };
    }

    /** Identifica un paso por su texto, que es como lo piensa quien lo escribió; el id interno no se muestra. */
    private etiquetaDeNodo(nodo: INodo, indice: number): string {
        const esInicial = nodo.id === ID_NODO_INICIAL;
        const prefijo = esInicial ? 'Primer paso' : `Paso ${indice + 1}`;
        const texto = String(nodo.texto || '').trim();

        if (!texto) {
            const tipo = NODO_TIPO_LABEL[nodo.tipo as NodoTipo] || '';
            return `${prefijo} — ${tipo}, todavía sin texto`;
        }

        const recortado = texto.length > LARGO_MAX_ETIQUETA_NODO
            ? `${texto.slice(0, LARGO_MAX_ETIQUETA_NODO)}…`
            : texto;

        return `${prefijo} — ${recortado}`;
    }
}
